import { useState, type FormEvent } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Alert, Button, Stack, TextInput } from "@mantine/core";
import { useAuth } from "../../auth/AuthContext";
import { errorMessage } from "../../api/errors";
import { AuthLayout } from "./AuthLayout";

interface FromState {
  from?: { pathname?: string };
}

export function LoginPage() {
  const {
    login,
    token,
    isAuthenticated,
    needsBrand,
    isSuperAdmin,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Already signed in → leave the auth screens.
  if (token && (isAuthenticated || (needsBrand && isSuperAdmin))) {
    const dest = (location.state as FromState | null)?.from?.pathname ?? "/";
    return <Navigate to={dest} replace />;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password);
      navigate("/", { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout title="Sign in" subtitle="Access your launch console">
      <form onSubmit={onSubmit} noValidate>
        <Stack gap="md">
          {error && (
            <Alert color="red" variant="light" role="alert">
              {error}
            </Alert>
          )}
          <TextInput
            label="Email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
            placeholder="you@company.com"
            required
            autoFocus
          />
          <TextInput
            label="Password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.currentTarget.value)}
            placeholder="••••••••"
            required
          />
          <Button type="submit" size="md" fullWidth loading={submitting}>
            Sign in
          </Button>
        </Stack>
      </form>
    </AuthLayout>
  );
}
